/**
 * "Install app" for the PWA. Chromium browsers fire `beforeinstallprompt` when
 * the app is installable; we hold on to the event and let the user trigger it
 * from a quiet entry in Settings. Safari never fires it (Add to Home Screen is
 * manual), so there the entry simply never appears.
 */
import { useSyncExternalStore } from 'react';

export type InstallState = 'not-available' | 'available' | 'installed' | 'dismissed';

export type InstallEvent =
  | { type: 'prompt-available' }
  | { type: 'accepted' }
  | { type: 'dismissed' }
  | { type: 'app-installed' };

/** Pure state machine; `installed` is terminal, `dismissed` waits for a fresh browser offer. */
export function installReducer(state: InstallState, event: InstallEvent): InstallState {
  if (state === 'installed') return state;
  switch (event.type) {
    case 'prompt-available':
      return 'available';
    case 'accepted':
    case 'app-installed':
      return 'installed';
    case 'dismissed':
      return state === 'available' ? 'dismissed' : state;
    default:
      return state;
  }
}

export function initialInstallState(standalone: boolean): InstallState {
  return standalone ? 'installed' : 'not-available';
}

/** Show the entry only while the browser has an offer we can act on. */
export function canOfferInstall(state: InstallState): boolean {
  return state === 'available';
}

// ------------------------------------------------------------- browser glue --

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let state: InstallState = 'not-available';
let deferred: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();

function dispatch(event: InstallEvent) {
  const next = installReducer(state, event);
  if (next === state) return;
  state = next;
  for (const l of listeners) l();
}

function isStandalone(): boolean {
  try {
    const nav = navigator as Navigator & { standalone?: boolean };
    return window.matchMedia('(display-mode: standalone)').matches || nav.standalone === true;
  } catch {
    return false;
  }
}

/** Call once at startup, before React renders, so an early event is not missed. */
export function initInstallPrompt(): void {
  if (typeof window === 'undefined') return;
  state = initialInstallState(isStandalone());
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferred = e as BeforeInstallPromptEvent;
    dispatch({ type: 'prompt-available' });
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    dispatch({ type: 'app-installed' });
  });
}

/** Show the browser's install dialog; a prompt event can only be used once. */
export async function promptInstall(): Promise<void> {
  const event = deferred;
  if (!event) return;
  deferred = null;
  try {
    await event.prompt();
    const { outcome } = await event.userChoice;
    dispatch({ type: outcome === 'accepted' ? 'accepted' : 'dismissed' });
  } catch {
    dispatch({ type: 'dismissed' });
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useInstallState(): InstallState {
  return useSyncExternalStore(
    subscribe,
    () => state,
    () => 'not-available',
  );
}
